/**
 * BusinessSelector -- Dropdown to pick the active business context.
 * Persists selection in localStorage and dispatches 'business:changed'
 * on window whenever the user switches.
 *
 * @module presentation/components/BusinessSelector
 */

import { BusinessApiClient } from '../../infrastructure/api/BusinessApiClient.js';
// La lectura del negocio elegido vive en infrastructure para que los clientes de API no importen esta pantalla
// (Ares #35231). Se reexporta: quien la importaba de aqui sigue igual.
import { STORAGE_KEY, STORAGE_NAME_KEY, isValidBusinessId, getSelectedBusiness } from '../../infrastructure/negocioElegido.js';
export { isValidBusinessId, getSelectedBusiness, getSelectedBusinessName } from '../../infrastructure/negocioElegido.js';

// DB-CONSOLIDATE-REFACTOR-PANEL-001 T1: el business_id canonico es varchar con prefijo
// biz_ (ej biz_01kwg69e... o biz_autoservicio_elmaizal_001), NO un UUID. Validamos el
// formato antes de persistir para atajar regresiones que reintroduzcan UUIDs legacy.
// El formato canonico (isValidBusinessId) vive en infrastructure/negocioElegido.js.
const LEGACY_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Persiste la seleccion validando formato. Rechaza UUID legacy (lo que este refactor
// elimina); avisa en formatos inesperados pero no rompe la seleccion del usuario.
function persistSelection(id, name) {
  if (LEGACY_UUID_RE.test(String(id || ''))) {
    console.warn(`[BusinessSelector] business_id con formato UUID legacy rechazado (esperado ^biz_): ${id}`);
    return false;
  }
  if (!isValidBusinessId(id)) {
    console.warn(`[BusinessSelector] business_id con formato inesperado (esperado ^biz_): ${id}`);
  }
  localStorage.setItem(STORAGE_KEY, id);
  if (name !== undefined && name !== null) localStorage.setItem(STORAGE_NAME_KEY, name);
  return true;
}

export async function setupBusinessSelector() {
  const $wrap = document.getElementById('nx-business-select-wrap');
  if (!$wrap) return;

  const result = await BusinessApiClient.list();
  const items = result.ok
    ? (result.data?.data?.items || result.data?.items || result.data || [])
    : [];

  const saved = getSelectedBusiness();

  // Si no hay valor guardado (o es inválido), auto-seleccionar el primero
  const firstItem = items[0];
  if (!saved || saved === 'undefined' || saved === 'null') {
    if (firstItem?.businessId) {
      persistSelection(firstItem.businessId, firstItem.displayName || firstItem.legalName || firstItem.businessId);
    }
  }

  const effective = localStorage.getItem(STORAGE_KEY);
  const locked = localStorage.getItem('nx_business_locked') === 'true';

  // If business is locked (negocio login), show as text only
  if (locked && effective) {
    const match = items.find(b => b.businessId === effective);
    const name = match?.displayName || match?.legalName || localStorage.getItem(STORAGE_NAME_KEY) || effective;
    $wrap.innerHTML = `<span style="padding:4px 10px;background:#21262d;border:1px solid #30363d;border-radius:4px;color:#e1e4e8;font-size:0.875rem;cursor:default" title="Negocio fijo">${name.length > 18 ? name.slice(0, 18) + '...' : name}</span>`;
    return;
  }

  const options = items.map(b => {
    const id = b.businessId;
    const selected = id === effective ? ' selected' : '';
    const label = b.displayName || b.legalName || id;
    return `<option value="${id}" data-name="${label}"${selected}>${label}</option>`;
  }).join('');

  $wrap.innerHTML = `<select id="nx-business-select">
    <option value="" disabled${!saved ? ' selected' : ''}>Negocio</option>
    ${options}
  </select>`;

  const $select = $wrap.querySelector('select');

  $select.addEventListener('change', () => {
    const id = $select.value;
    if (!id) return;
    const name = $select.selectedOptions[0]?.dataset.name || id;
    const previousBusinessId = localStorage.getItem(STORAGE_KEY) || null;
    // T1: rechaza persistir un UUID legacy; si es formato inesperado avisa pero continua.
    if (!persistSelection(id, name)) return;
    window.dispatchEvent(new CustomEvent('business:changed', {
      detail: { businessId: id, businessName: name, previousBusinessId }
    }));
  });
}
